import { useCallback, useState } from "react";
import { FaCommentAlt, FaRegArrowAltCircleRight } from "react-icons/fa";
import { HiOutlineEye } from "react-icons/hi2";
import { Link } from "react-router-dom";
import { ActionIcon, Text, Tooltip } from "rizzui";
import { toast } from "sonner";
import DeletePopover from "../../../components/buttons/DeletePopover";
import StatusDropdown from "../../../components/forms/StatusDropdown";
import { Loader } from "../../../components/loader/SpinnerLogo";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import { useFiltersPR } from "../../../components/providers/FiltersProvider";
import CustomTable from "../../../components/table/CustomTable";
import { FilterDrawerView } from "../../../components/table/components/TableFilter";
import useKoReasons from "../../../hooks/api-calls/useKoReasons";
import useLeadStates from "../../../hooks/api-calls/leads/useLeadStates";
import useFetch from "../../../hooks/useFetch";
import useHandleErrors from "../../../hooks/useHandleErrors";
import Page from '../../../layout/Page/Page';
import { menuRoutes } from "../../../router/menu";
import { LeadsService } from "../../../services/leads/leadsService";
import { Lead, LeadsApiResponse } from "../../../type/entities/leads-type";
import { AddCommentModal } from "../../clients/list/components/AddCommentModal";
import { ContractProvider } from "../../contracts/providers/ContractProvider";
import { renewalSettingsOptions } from "../../settings/product-types/ProductTypeModalForm";
import ConvertToContractModal from "../components/ConvertToContractModal";
import LeadFilters from "./LeadFilters";

interface ListLeadsProps { }

const columnOrder = [
    'id',
    'currentStatusId',
    'currentStatusName',
    'currentStatusDate',
    'currentStatusColor',
    'originalContractCode',
    'originalContractId',
    'generatedContractCode',
    'generatedContractId',
    'productTypeName',
    'productName',
    'origin',
    'clientCompanyName',
    'clientId',
    'agent',
    'userId',
    'userName',
    'userLastName',
    'userEmail',
    'commercialNetworkName',
    'commercialNetworkCode',
    'departmentName',
    'renewalSettings',
    'productTypeRenewalSettings',
    'productRenewalSettings',
    'targetDate',
];

const ListLeads: React.FC<ListLeadsProps> = ({ }) => {

    // HOOKS

    const service = new LeadsService();
    const { userCan } = usePrivilege();
    const { handleErrors } = useHandleErrors();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const { getLeadStatesList } = useLeadStates();
    const { koReasonsOptions } = useKoReasons();

    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [rowId, setRowId] = useState<string>('');
    const [openCommentModal, setOpenCommentModal] = useState<boolean>(false);
    const [openConvertModal, setOpenConvertModal] = useState<boolean>(false);
    const [selectedLead, setSelectedLead] = useState<Lead | null>(null);

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listLeads(filters);
        return response.getResponseData() as LeadsApiResponse;
    }, [filters]));

    /**
     * Order the data to match the column order
     */
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ABRIR MODAL DE CONVERSIÓN A CONTRATO
     * @EN OPEN CONVERT TO CONTRACT MODAL
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleOpenConvertModal = (lead: Lead) => {
        setSelectedLead(lead);
        setOpenConvertModal(true);
    };

    const handleCloseConvertModal = () => {
        setOpenConvertModal(false);
        setSelectedLead(null);
    };

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR LEAD
     * @EN DELETE LEAD
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteLead(id)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Lead eliminado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR MÚLTIPLES LEADS
     * @EN DELETE MULTIPLE LEADS
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        try {
            const response = await (await service.deleteMultipleLeads(ids)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Leads eliminados correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CAMBIAR ESTADO
     * @EN CHANGE STATUS
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleStateChange = async (id: string, stateId: string, comment: string, koReasonId?: string) => {
        var response = { success: false };
        try {
            response = await (await service.changeLeadStatus(id, stateId, comment, koReasonId)).getResponseData();
            if (response.success) {
                toast.success('Estado del lead actualizado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            toast.error('Error al actualizar el estado');
        }
        refetch();
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    return (
        <Page>
            {data?.data && (
                <CustomTable
                    largeTable
                    id={'leads-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    columnsNotShown={[
                        'currentStatusId',
                        'currentStatusDate',
                        'currentStatusColor',
                        'originalContractId',
                        'generatedContractId',
                        'clientId',
                        'userId',
                        'userName',
                        'userLastName',
                        'userEmail',
                        'commercialNetworkCode',
                        'productTypeRenewalSettings',
                        'productRenewalSettings',
                    ]}
                    overrideColumns={[
                        {
                            key: 'currentStatusName',
                            render: (row: any) => {
                                const currentStatus = {
                                    id: row.row.original.currentStatusId,
                                    name: row.row.original.currentStatusName,
                                    color: row.row.original.currentStatusColor,
                                    date: row.row.original.currentStatusDate?.date
                                };
                                const canChangeStatus = userCan('change_leads_status', 'leads');
                                return (
                                    <StatusDropdown
                                        disabled={!canChangeStatus}
                                        entityId={row.row.original?.id}
                                        title="¿Estás seguro de cambiar el estado del vencimiento?"
                                        statesOptions={getLeadStatesList(row.row.original?.currentStatusId)}
                                        currentState={currentStatus}
                                        currentStateDate={row.row.original?.currentStatusDate?.date}
                                        handleStateChange={handleStateChange}
                                        koReasonsOptions={koReasonsOptions}
                                        koReasonStatusEntity="lead"
                                    />
                                )
                            }
                        },
                        {
                            key: 'originalContractCode',
                            render: (row: any) => (
                                userCan('get_contracts', 'contracts')
                                    ? (
                                        <Link to={`${menuRoutes.contracts.path}/${row.row.original.originalContractId}/profile${menuRoutes.contracts.profile.info}`}>
                                            <Text className='font-bold text-primary'>{row.row.original.originalContractCode}</Text>
                                        </Link>
                                    )
                                    : <Text className='font-bold text-primary'>{row.row.original.originalContractCode}</Text>
                            )
                        },
                        {
                            key: 'generatedContractCode',
                            render: (row: any) => (
                                userCan('get_contracts', 'contracts') && row.row.original.generatedContractId
                                    ? (
                                        <Link to={`${menuRoutes.contracts.path}/${row.row.original.generatedContractId}/profile${menuRoutes.contracts.profile.info}`}>
                                            <Text className='font-bold text-primary'>{row.row.original.generatedContractCode}</Text>
                                        </Link>
                                    )
                                    : <Text>{row.row.original.generatedContractCode || 'N/A'}</Text>
                            )
                        },
                        {
                            key: 'productName',
                            render: (row: any) => {
                                const product = row.row.original?.productName;
                                return (
                                    <div className='flex flex-col'>
                                        {product
                                            ? (<Link
                                                to={`${menuRoutes.products.path}?filter_filters=%7B"active"%3Atrue%2C"search_text"%3A"${product}"%7D&filter_order=%5B%5D&page=1&limit=50`}
                                                className='font-bold text-primary'
                                            >
                                                {product}
                                            </Link>)
                                            : 'N/A'
                                        }
                                    </div>
                                )
                            }
                        },
                        {
                            key: 'clientCompanyName',
                            render: (row: any) => (
                                row.row.original.clientCompanyName
                                    ? userCan('get_clients', 'clients')
                                        ? (
                                            <Link to={`${menuRoutes.clients.path}/${row.row.original.clientId}/profile${menuRoutes.clients.profile.info}`}>
                                                <span className="font-bold text-primary">{row.row.original.clientCompanyName}</span>
                                            </Link>
                                        )
                                        : <span className="font-bold text-primary">{row.row.original.clientCompanyName}</span>
                                    : 'N/A'
                            )
                        },
                        {
                            key: 'agent',
                            render: (row: any) => (
                                <div className="flex flex-col">
                                    {userCan('get_user', 'user')
                                        ? (
                                            <Link to={`${menuRoutes.users.path}/${row.row.original.userId}/profile${menuRoutes.users.profile.info}`} className="font-bold text-primary">
                                                {row.row.original.userName} {row.row.original.userLastName || ''}
                                            </Link>
                                        )
                                        : <span className="font-medium">{row.row.original.userName} {row.row.original.userLastName || ''}</span>
                                    }
                                    <div className="text-xs text-gray-500">{row.row.original.userEmail}</div>
                                </div>
                            )
                        },
                        {
                            key: 'commercialNetworkName',
                            render: (row: any) => (
                                <div className="flex flex-col">
                                    <span className="font-medium">{row.row.original.commercialNetworkName}</span>
                                    <span className="text-xs text-gray-500">{row.row.original.commercialNetworkCode}</span>
                                </div>
                            )
                        },
                        {
                            key: 'origin',
                            render: (row: any) => (
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${row.row.original.origin === 'auto_renovation'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-green-100 text-green-800'
                                    }`}>
                                    {row.row.original.origin === 'auto_renovation' ? 'Renovación Automática' : 'Renovación Manual'}
                                </span>
                            )
                        },
                        {
                            key: 'renewalSettings',
                            render: (row: any) => (
                                renewalSettingsOptions.find(option => option.value === (row.row.original.productTypeRenewalSettings || row.row.original.productRenewalSettings))?.label || 'N/A'
                            )
                        },
                        {
                            key: 'targetDate',
                            render: (row: any) => {
                                const date = row.row.original.targetDate;
                                return date ? (new Date(date).toLocaleDateString()) : 'N/A';
                            }
                        },
                    ]}
                    actions={[
                        {
                            label: 'Convert to Contract',
                            render: (row: any) => {
                                // If the lead is in a satisfactory or unsatisfactory status, do not show the button
                                if (getLeadStatesList().find(state => state.value === row.currentStatusId)?.satisfactoryStatus || getLeadStatesList().find(state => state.value === row.currentStatusId)?.unsatisfactoryStatus) {
                                    return null;
                                }
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Convertir a contrato'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                                            onClick={() => handleOpenConvertModal(row)}
                                        >
                                            <FaRegArrowAltCircleRight className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Add Comment',
                            render: (row: any) => {
                                const canAddComment = userCan('edit_leads', 'leads');
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Añadir comentario'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            disabled={!canAddComment}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                                            onClick={() => { setRowId(row.id); setOpenCommentModal(true) }}
                                        >
                                            <FaCommentAlt className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'View',
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Ver renovación'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link to={`${menuRoutes.leads.path}/${row.id}/profile${menuRoutes.leads.profile.info}`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700"
                                            >
                                                <HiOutlineEye className="h-4 w-4" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            }
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'leads', permission: 'delete_leads' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                title={`Eliminar renovación`}
                                                description={`¿Estás seguro de que deseas eliminar la renovación?`}
                                                onDelete={() => handleDelete(row.id)}
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    handleMultipleDelete={handleMultiDelete}
                    multiDeletePermission={{ group: 'leads', action: 'delete_leads' }}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as LeadsApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            )}

            {!data?.data && loading && (<Loader height='70vh' />)}

            <AddCommentModal service={(new LeadsService).addLeadComment} id={rowId} isOpen={openCommentModal} onClose={() => setOpenCommentModal(false)} refetch={refetch} />

            <ContractProvider>
                <ConvertToContractModal
                    isOpen={openConvertModal}
                    onClose={handleCloseConvertModal}
                    lead={selectedLead}
                    onSuccess={() => refetch()}
                />
            </ContractProvider>

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros de Renovaciones'}>
                <LeadFilters filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} />
            </FilterDrawerView>
        </Page>
    );
};

export default ListLeads;